"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import type { CustomerLanguage } from "@/lib/customer-language";

interface PhaseDef {
  id: string;
  name: string;
  sanskrit: string;
  duration: string;
  houseRel: string;
  nature:
    "Rising Transition" | "Peak Transformation" | "Setting Resolution" | "Dhaiya Minor Panoti";
  mindImpact: string;
  careerImpact: string;
  wealthImpact: string;
  remedies: string[];
}

const PHASES: PhaseDef[] = [
  {
    id: "phase1",
    name: "Phase 1: Rising Phase",
    sanskrit: "आद्य चरण (12th House)",
    duration: "2.5 Years (1st Quarter)",
    houseRel: "12th House counted from Natal Moon",
    nature: "Rising Transition",
    mindImpact:
      "Increased mental restlessness, vivid dreams, subconscious anxiety, and sleep pattern shifts.",
    careerImpact:
      "Foreign connections activate; feeling unacknowledged at workplace despite putting in solid hours.",
    wealthImpact:
      "Higher unexpected expenditure on health, travel, or family commitments; need for disciplined budgeting.",
    remedies: [
      "Light a mustard oil lamp under a Peepal tree on Saturday evenings",
      "Chant 'Om Sham Shanaischaraya Namah' 108 times at twilight",
      "Feed birds and crows daily in the morning",
    ],
  },
  {
    id: "phase2",
    name: "Phase 2: Peak Phase",
    sanskrit: "मध्य चरण (1st House / Moon)",
    duration: "2.5 Years (2nd Quarter)",
    houseRel: "Saturn transits directly over Natal Moon",
    nature: "Peak Transformation",
    mindImpact:
      "Deep psychological testing, ego shedding, and building enduring emotional resilience.",
    careerImpact:
      "Heavy work responsibilities; complete professional restructuring leading to lasting authority.",
    wealthImpact:
      "Conservative financial growth; rewards arrive slowly but stay permanently established.",
    remedies: [
      "Recite Hanuman Chalisa daily, especially on Tuesdays and Saturdays",
      "Perform selfless seva or donate food to laborers and the elderly",
      "Avoid starting speculative gambling or unethical shortcuts",
    ],
  },
  {
    id: "phase3",
    name: "Phase 3: Setting Phase",
    sanskrit: "अंत्य चरण (2nd House)",
    duration: "2.5 Years (3rd Quarter)",
    houseRel: "2nd House counted from Natal Moon",
    nature: "Setting Resolution",
    mindImpact:
      "Mental clarity returns; emotional peace and relief from previous peak-phase pressures.",
    careerImpact:
      "Fruit of 5 years of hard work begins to materialize; promotions and stable recognition arrive.",
    wealthImpact:
      "Recovery of financial savings; investments begin yielding disciplined, stable returns.",
    remedies: [
      "Donate black sesame (til) and iron utensils on Saturdays",
      "Offer water to Shivling on Monday and Saturday mornings",
      "Maintain respectful and truthful speech within the family",
    ],
  },
  {
    id: "kantaka",
    name: "Kantaka Shani (4th Dhaiya)",
    sanskrit: "कंटक शनि (4th House)",
    duration: "2.5 Years (Minor Panoti)",
    houseRel: "4th House from Moon (Chest / Home)",
    nature: "Dhaiya Minor Panoti",
    mindImpact:
      "Domestic disputes or temporary lack of happiness at home; mother requires healthcare support.",
    careerImpact:
      "Work relocations, changes of office desk, or temporary professional distance from hometown.",
    wealthImpact:
      "Caution required when purchasing real estate or executing vehicular transactions.",
    remedies: [
      "Recite Shani Chalisa every Saturday evening after sunset",
      "Offer sweet milk to the roots of a Banyan or Peepal tree",
      "Avoid disputes with domestic help and service workers",
    ],
  },
  {
    id: "ashtama",
    name: "Ashtama Shani (8th Dhaiya)",
    sanskrit: "अष्टम शनि (8th House)",
    duration: "2.5 Years (Minor Panoti)",
    houseRel: "8th House from Moon (Transformation)",
    nature: "Dhaiya Minor Panoti",
    mindImpact:
      "Occasional fear of unknown events; intense pull toward spirituality, occult, and meditation.",
    careerImpact:
      "Sudden unexpected shifts in career domain; requires extreme honesty and avoidance of shortcuts.",
    wealthImpact:
      "Delay in receiving ancestral inheritance; avoid taking unsecured high-interest loans.",
    remedies: [
      "Chant Maha Mrityunjaya Mantra (108 times daily)",
      "Recite Dasharatha Shani Stotram on Saturdays",
      "Donate footwear (leather/rubber shoes) to needy workers",
    ],
  },
];

/** Hindi labels for each phase; the impact and remedy text stays English. */
const PHASE_LABELS_HI: Record<
  string,
  {
    name: string;
    short: string;
    sanskrit: string;
    duration: string;
    houseRel: string;
    nature: string;
  }
> = {
  phase1: {
    name: "पहला चरण: उदय चरण",
    short: "पहला चरण",
    sanskrit: "आद्य चरण (12वाँ भाव)",
    duration: "2.5 वर्ष (पहली चौथाई)",
    houseRel: "जन्म चंद्र से 12वाँ भाव",
    nature: "उदय चरण",
  },
  phase2: {
    name: "दूसरा चरण: चरम चरण",
    short: "दूसरा चरण",
    sanskrit: "मध्य चरण (पहला भाव / चंद्र)",
    duration: "2.5 वर्ष (दूसरी चौथाई)",
    houseRel: "जन्म चंद्र पर शनि का गोचर",
    nature: "चरम परिवर्तन",
  },
  phase3: {
    name: "तीसरा चरण: अस्त चरण",
    short: "तीसरा चरण",
    sanskrit: "अंत्य चरण (दूसरा भाव)",
    duration: "2.5 वर्ष (तीसरी चौथाई)",
    houseRel: "जन्म चंद्र से दूसरा भाव",
    nature: "समापन चरण",
  },
  kantaka: {
    name: "कंटक शनि (चौथे भाव की ढैया)",
    short: "कंटक शनि",
    sanskrit: "कंटक शनि (चौथा भाव)",
    duration: "2.5 वर्ष (लघु पनौती)",
    houseRel: "चंद्र से चौथा भाव (छाती / घर)",
    nature: "ढैया लघु पनौती",
  },
  ashtama: {
    name: "अष्टम शनि (आठवें भाव की ढैया)",
    short: "अष्टम शनि",
    sanskrit: "अष्टम शनि (आठवाँ भाव)",
    duration: "2.5 वर्ष (लघु पनौती)",
    houseRel: "चंद्र से आठवाँ भाव (परिवर्तन)",
    nature: "ढैया लघु पनौती",
  },
};

const STUDIO_LABELS = {
  en: {
    eyebrow: "Shani Transit Timeline",
    heading: "7.5-Year Shani Sade Sati & Dhaiya Calculator (शनि साढ़े साती)",
    position: "Astronomical Position:",
    duration: "Duration:",
    checkPhase: "Check My Current Phase ✦",
    mind: "Mind & Emotions",
    career: "Career & Status",
    wealth: "Wealth & Finances",
    remedies: "Prescribed Shani Upay & Remedial Actions",
    openCalculator: "Open Free Sade Sati Calculator →",
  },
  hi: {
    eyebrow: "शनि गोचर समयरेखा",
    heading: "7.5 वर्ष की शनि साढ़े साती और ढैया कैलकुलेटर",
    position: "खगोलीय स्थिति:",
    duration: "अवधि:",
    checkPhase: "मेरा वर्तमान चरण देखें ✦",
    mind: "मन और भावनाएँ",
    career: "करियर और प्रतिष्ठा",
    wealth: "धन और वित्त",
    remedies: "शनि के उपाय",
    openCalculator: "मुफ्त साढ़े साती कैलकुलेटर खोलें →",
  },
} as const;

/** The phase's visible labels in `language`; English keeps the PhaseDef text. */
function phaseLabels(phase: PhaseDef, language: CustomerLanguage) {
  const hi = language === "hi" ? PHASE_LABELS_HI[phase.id] : undefined;
  if (hi) return hi;
  return {
    name: phase.name,
    short: phase.name.split(" ")[0],
    sanskrit: phase.sanskrit,
    duration: phase.duration,
    houseRel: phase.houseRel,
    nature: phase.nature,
  };
}

export default function SadeSatiTimelineStudio({
  language = "en",
}: {
  language?: CustomerLanguage;
}) {
  const [selectedId, setSelectedId] = useState<string>("phase2");

  const phase = useMemo<PhaseDef>(() => {
    return PHASES.find((p) => p.id === selectedId) || PHASES[1];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `Please check if my Janam Kundali is currently running ${phase.name} (${phase.sanskrit}). What are the exact transit dates and authentic Shani remedies for my chart?`;
    try {
      safeSessionSet("seo_entry_intent", "sade_sati_lookup");
      safeSessionSet("seo_entry_path", "/sade-sati");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader language={language} />
      <PhaseSelector selectedId={selectedId} onSelect={setSelectedId} language={language} />
      <PhaseDetailCard phase={phase} onConsult={handleConsult} language={language} />
    </div>
  );
}

function StudioHeader({ language }: { language: CustomerLanguage }) {
  const labels = STUDIO_LABELS[language];
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        {labels.eyebrow}
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">{labels.heading}</h2>
      <p className="text-body-small text-text-secondary mt-1">
        Check your current Sade Sati phase (Rising, Peak, Setting) and Dhaiya transits to understand
        life impacts and authentic remedies.
      </p>
    </div>
  );
}

function PhaseSelector({
  selectedId,
  onSelect,
  language,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
  language: CustomerLanguage;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {PHASES.map((p) => {
          const isSelected = p.id === selectedId;
          const labels = phaseLabels(p, language);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="block text-caption font-bold text-intent-brand">
                {labels.duration.split(" ")[0]} {labels.duration.split(" ")[1]}
              </span>
              <span className="block text-body-small font-bold text-text-primary">
                {labels.short}
              </span>
              <span className="block text-caption text-text-muted truncate">
                {p.sanskrit.split(" ")[0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PhaseDetailCard({
  phase,
  onConsult,
  language,
}: {
  phase: PhaseDef;
  onConsult: () => void;
  language: CustomerLanguage;
}) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/sade-sati"),
    "/sade-sati",
    language
  );
  const labels = STUDIO_LABELS[language];
  const phaseText = phaseLabels(phase, language);

  const badgeColor =
    phase.nature === "Peak Transformation"
      ? "bg-warning/10 text-warning"
      : phase.nature === "Setting Resolution"
        ? "bg-success/10 text-success"
        : "bg-intent-brand/10 text-intent-brand";

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              🪐 {phaseText.name} ({phaseText.sanskrit})
            </h3>
            <span className={`px-3 py-0.5 rounded-full text-caption font-bold ${badgeColor}`}>
              {phaseText.nature}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            {labels.position} <strong className="text-text-primary">{phaseText.houseRel}</strong>{" "}
            &middot; {labels.duration}{" "}
            <span className="font-semibold text-text-primary">{phaseText.duration}</span>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          {labels.checkPhase}
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            {labels.mind}
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{phase.mindImpact}</p>
        </div>

        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            {labels.career}
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{phase.careerImpact}</p>
        </div>

        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            {labels.wealth}
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{phase.wealthImpact}</p>
        </div>
      </div>

      <div className="mt-4 p-4 rounded-xl bg-intent-brand/5 border border-intent-brand/20">
        <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-2">
          {labels.remedies}
        </p>
        <ul className="space-y-1 text-caption text-text-primary">
          {phase.remedies.map((rem, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span className="text-intent-brand">✓</span>
              <span>{rem}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Know your Moon sign to calculate exact Sade Sati entry and exit dates.
        </span>
        <Link
          href={language === "hi" ? "/hi/sade-sati/" : "/sade-sati/"}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          {labels.openCalculator}
        </Link>
      </div>
    </div>
  );
}
